import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useRoom } from '../hooks/useRoom';
import { useScores } from '../hooks/useScores';
import { useReactions } from '../hooks/useReactions';
import { usePresence } from '../hooks/usePresence';
import { useMyRooms } from '../hooks/useMyRooms';
import { useGlobalLoad } from '../hooks/useGlobalLoad';
import Voting from './Voting';
import Results from './Results';
import MyVotesResults from './MyVotesResults';
import UserManagement from './UserManagement';
import LoadingSpinner from './LoadingSpinner';
import { EMOJI_REACTIONS, COUNTRY_CODES } from '../constants';
import { doc, setDoc, getDocs, collection, deleteDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import ReactCountryFlag from 'react-country-flag';

const TabIcon = ({ type, active }) => {
  const color = active ? 'var(--pink)' : 'var(--text3)';
  if (type === 'voting') return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
    </svg>
  );
  if (type === 'results') return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 20V10M12 20V4M6 20v-6"/>
    </svg>
  );
  if (type === 'myvotes') return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8z"/>
    </svg>
  );
  return null;
};

const RoomPage = ({ user, contests, users, setUsers, onToggleTheme }) => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { room, members, memberIds, loading: roomLoading } = useRoom(roomId);
  const activeContest = room ? contests.find(c => c.id === room.contestId) : null;

  const {
    allScores, loading: scoresLoading, lastChanged, getScore, submitScore,
    deleteScore, clearAllScores,
  } = useScores(room?.contestId, user);

  const [activeTab, setActiveTab] = useState('voting');
  const [rankingsScope, setRankingsScope] = useState(roomId); // room id | 'global'
  const [resultsContestId, setResultsContestId] = useState(null); // null = room's own contest

  const browseContestId = resultsContestId && resultsContestId !== room?.contestId ? resultsContestId : null;
  const { allScores: browseScores } = useScores(browseContestId, user);

  const { isLive, markActive } = usePresence(roomId, user?.id);
  const { myRooms, allMyRoomIds } = useMyRooms(user?.id, room?.contestId);
  const { activeCount } = useGlobalLoad(true);
  const reactionMode = activeCount >= 100 ? 'degraded' : 'global';
  const { reactions, sendReaction } = useReactions(roomId, { mode: reactionMode, myRoomIds: allMyRoomIds });
  const [showMembers, setShowMembers] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [page, setPage] = useState('main');
  const [copied, setCopied] = useState(false);
  const [pressedEmoji, setPressedEmoji] = useState(null);
  const [contestLive, setContestLive] = useState(null);

  useEffect(() => {
    if (!room?.contestId) return;
    const unsub = onSnapshot(doc(db, 'contests', room.contestId), (snap) => {
      if (snap.exists()) setContestLive(snap.data());
    });
    return unsub;
  }, [room?.contestId]);

  // Reset rankings scope to current room when navigating between rooms
  useEffect(() => { setRankingsScope(roomId); }, [roomId]);

  // Reset to EVERYONE when switching to a past/browse contest so stale room filter doesn't hide all scores
  useEffect(() => {
    if (resultsContestId && resultsContestId !== room?.contestId) {
      setRankingsScope('global');
    } else if (!resultsContestId) {
      setRankingsScope(roomId);
    }
  }, [resultsContestId, room?.contestId, roomId]);

  const isLoading = roomLoading || scoresLoading;

  // Ticker entries are latched \u2014 regenerates only when the active contestant changes
  // or on a 20s interval, NOT on every Firestore snapshot. This prevents the
  // CSS scroll animation from resetting mid-loop every time someone votes.
  const [tickerEntries, setTickerEntries] = useState(null); // null = no ticker; [] = waiting; [{rank,name,avg,arrow}]
  const prevRanksRef = useRef({});

  const computeTickerEntries = useCallback(() => {
    if (!activeContest || !allScores) return null;
    const scored = activeContest.contestants
      .map(c => {
        const scores = allScores[c.id] || {};
        const voters = Object.values(scores).filter(s => s.overall !== undefined);
        if (voters.length === 0) return null;
        const avg = voters.reduce((sum, s) => sum + s.overall, 0) / voters.length;
        return { id: c.id, name: (c.country || c.name).toUpperCase(), avg: avg.toFixed(1) };
      })
      .filter(Boolean)
      .sort((a, b) => b.avg - a.avg);

    if (scored.length === 0) return { entries: [], unvoted: activeContest.contestants.length };

    const prev = prevRanksRef.current;
    const entries = scored.slice(0, 10).map((s, idx) => {
      const rank = idx + 1;
      const prevRank = prev[s.id];
      let arrow = '';
      if (prevRank !== undefined && prevRank !== rank) {
        arrow = prevRank > rank ? ' \u25b2' : ' \u25bc';
      }
      return { rank, name: s.name, avg: s.avg, arrow };
    });

    const nextRanks = {};
    scored.forEach((s, idx) => { nextRanks[s.id] = idx + 1; });
    prevRanksRef.current = nextRanks;

    const unvoted = activeContest.contestants.length - scored.length;
    return { entries, unvoted };
  }, [activeContest, allScores]);

  // Latch ticker entries: recompute when active contestant advances
  React.useEffect(() => {
    setTickerEntries(computeTickerEntries());
  }, [contestLive?.activeContestantId, computeTickerEntries]);

  // Safety refresh every 20s so ticker data isn't older than one song
  React.useEffect(() => {
    const id = setInterval(() => setTickerEntries(computeTickerEntries()), 20000);
    return () => clearInterval(id);
  }, [computeTickerEntries]);

  const copyCode = () => {
    const text = room?.code || '';
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
    } else {
      const input = document.createElement('input');
      input.value = text;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const toggleContestStatus = async () => {
    if (!user?.isAdmin || !activeContest) return;
    try {
      await setDoc(doc(db, "contests", room.contestId), { closed: !activeContest.closed }, { merge: true });
      window.location.reload();
    } catch (error) { alert("Failed to update contest status."); }
  };

  const handleClearCompetition = async () => {
    if (!user?.isAdmin) return;
    if (!window.confirm(`Clear all scores for ${activeContest?.name}?`)) return;
    try { await clearAllScores(); } catch (error) { alert("Failed to clear scores."); }
  };

  const handleDeleteVote = async (userId, contestId, contestantId) => {
    if (!user?.isAdmin) return;
    try { await deleteScore(userId, contestantId); } catch (error) { alert("Failed to delete vote."); }
  };

  const handleSubmitScore = async (contestantId, criterionId, value) => {
    await submitScore(contestantId, criterionId, value);
    markActive();
  };

  const isHost = room && user && room.createdBy === user.id;

  const handleKickMember = async (memberId) => {
    if (!isHost || memberId === user.id || memberId === room.createdBy) return;
    if (!window.confirm('Kick this member from the room?')) return;
    try {
      await deleteDoc(doc(db, 'rooms', roomId, 'members', memberId));
      const userDoc = await import('firebase/firestore').then(m => m.getDoc(doc(db, 'users', memberId)));
      const current = userDoc.data()?.roomIds || [];
      const updated = current.filter(r => r !== roomId);
      await setDoc(doc(db, 'users', memberId), { roomIds: updated }, { merge: true });
    } catch (e) {
      alert('Failed to kick: ' + e.message);
    }
  };

  const handleDeleteRoom = async () => {
    if (!isHost) return;
    if (!window.confirm(`Delete "${room.name}"? All members will be removed. This cannot be undone.`)) return;
    try {
      const firestore = await import('firebase/firestore');
      // Remove this room from every member's roomIds
      const membersSnap = await firestore.getDocs(firestore.collection(db, 'rooms', roomId, 'members'));
      for (const m of membersSnap.docs) {
        const uDoc = await firestore.getDoc(doc(db, 'users', m.id));
        const rids = (uDoc.data()?.roomIds || []).filter(r => r !== roomId);
        await setDoc(doc(db, 'users', m.id), { roomIds: rids }, { merge: true });
      }
      // Delete subcollections
      for (const sub of ['members', 'presence', 'reactions']) {
        const snap = await firestore.getDocs(firestore.collection(db, 'rooms', roomId, sub));
        for (const d of snap.docs) await deleteDoc(d.ref);
      }
      await deleteDoc(doc(db, 'rooms', roomId));
      navigate('/');
    } catch (e) {
      alert('Failed to delete: ' + e.message);
    }
  };

  const handleEmojiReact = (emojiId) => {
    const emoji = EMOJI_REACTIONS.find(e => e.id === emojiId);
    if (!emoji) return;
    setPressedEmoji(emojiId);
    setTimeout(() => setPressedEmoji(null), 150);
    const targetContestantId = contestLive?.activeContestantId || null;
    sendReaction(user.id, user.name, emoji.emoji, targetContestantId);
  };

  const myScores = {};
  if (user && activeContest) {
    activeContest.contestants.forEach(c => {
      const score = allScores[c.id]?.[user.id];
      if (score) myScores[c.id] = score;
    });
  }

  useEffect(() => {
    const vOpen = !!contestLive?.activeContestantId && !contestLive?.closed;
    if (contestLive !== null && !vOpen) {
      setActiveTab(prev => prev === 'voting' ? 'results' : prev);
    }
  }, [contestLive]);

  if (roomLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg)' }}>
        <LoadingSpinner />
      </div>
    );
  }

  if (!room) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--bg)' }}>
        <div className="p-6 text-center" style={{ border: '1px solid var(--border)' }}>
          <p className="mb-4" style={{ color: 'var(--text2)' }}>Room not found</p>
          <button
            onClick={() => navigate('/')}
            className="px-5 py-2.5 text-white font-semibold text-sm"
            style={{ background: 'linear-gradient(90deg, #ff2d87, #7c3aed)', borderRadius: 0, minHeight: 44 }}
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  if (page === 'userManagement' && user.isAdmin) {
    const deleteUser = async (userId) => {
      if (!window.confirm("Delete this user and their ratings?")) return;
      try { await deleteDoc(doc(db, "users", userId)); } catch (e) {}
      const snap = await getDocs(collection(db, "users"));
      setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    };
    return <UserManagement users={users} currentUser={user} deleteUser={deleteUser} goBack={() => setPage('main')} />;
  }

  const votingOpen = !!contestLive?.activeContestantId && !contestLive?.closed;

  const tabs = [];
  tabs.push({ id: 'voting', label: 'RATE', icon: 'voting' });
  tabs.push({ id: 'results', label: 'RANKINGS', icon: 'results' });
  tabs.push({ id: 'myvotes', label: 'MY SCORES', icon: 'myvotes' });

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg)' }}>
      {/* Full-screen emoji fly overlay — zoom-reactions style, floats over everything */}
      <div
        className="fixed inset-0 pointer-events-none overflow-hidden"
        style={{ zIndex: 45 }}
        aria-hidden="true"
      >
        {(() => {
          const now = Date.now();
          return reactions
            .filter(r => {
              const ts = r.timestamp?.toDate?.()?.getTime?.() || now;
              return now - ts <= 5500;
            })
            .sort((a, b) => {
              const ta = a.timestamp?.toDate?.()?.getTime?.() || 0;
              const tb = b.timestamp?.toDate?.()?.getTime?.() || 0;
              return tb - ta;
            })
            .slice(0, 60);
        })().map((r, i) => {
          const ts = r.timestamp?.toDate?.()?.getTime?.() || Date.now();
          const ageMs = Date.now() - ts;
          if (ageMs > 5500) return null;
          const seed = ((r.id?.charCodeAt(0) || 0) + (r.id?.charCodeAt(3) || 0) + ts) | 0;
          const leftPct = 10 + Math.abs(seed * 9301 + 49297) % 80;                // 10-90% — full viewport width
          const startBottomPct = Math.abs(seed * 1777 + 2011) % 60;               // 0-59% from bottom — random start height
          const driftXvw = ((seed * 31) % 40) - 20;                               // -20..+20 vw (no bias)
          const driftXMidVw = driftXvw / 3;
          const driftYvh = -(35 + (Math.abs(seed * 7) % 50));                     // -35..-85 vh
          const rotEnd = ((seed * 13) % 360) - 180;
          const rotMid = rotEnd / 4;
          const scaleEnd = 0.7 + ((Math.abs(seed) % 80) / 100);                    // 0.7..1.5
          const dur = 4.2 + ((Math.abs(seed * 3) % 140) / 100);                    // 4.2..5.6s
          const fontSize = 28 + ((Math.abs(seed * 17) % 28));                      // 28..56 px
          return (
            <span
              key={r.id}
              className="emoji-burst absolute"
              style={{
                left: `${leftPct}%`,
                bottom: `${startBottomPct}%`,
                fontSize: `${fontSize}px`,
                lineHeight: 1,
                '--drift-x': `${driftXvw}vw`,
                '--drift-x-mid': `${driftXMidVw}vw`,
                '--drift-y': `${driftYvh}vh`,
                '--rot-mid': `${rotMid}deg`,
                '--rot-end': `${rotEnd}deg`,
                '--scale-end': scaleEnd,
                '--float-dur': `${dur}s`,
                animationDelay: `${(i % 6) * 0.05}s`,
                filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.25))',
              }}
            >
              {r.emoji}
            </span>
          );
        })}
      </div>

      <div className="max-w-2xl mx-auto">
        {/* Sticky group — header + ticker + now playing + emoji bar pinned together */}
        <div className="sticky top-0 z-40" style={{ background: 'var(--bg)' }}>
        {/* Header */}
        <header
          className="px-4 py-2.5"
          style={{
            background: 'var(--bg)',
            borderBottom: '1px solid var(--border)',
            backdropFilter: 'blur(12px)',
          }}
        >
          {isLoading && <div className="absolute inset-x-0 top-0 h-0.5 loading-bar" style={{ background: 'rgba(255, 45, 135, 0.3)' }} />}

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5 min-w-0">
              <button
                onClick={() => navigate('/')}
                className="p-1 transition-colors"
                style={{ color: 'var(--text3)', minHeight: 44, minWidth: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h1 className="font-display text-sm truncate" style={{ fontWeight: 600, color: 'var(--text)' }}>{room.name}</h1>
                  {isLive && (
                    <span
                      className="live-pulse font-mono text-[10px] px-1.5 py-0.5 text-white"
                      style={{ background: '#ef4444', fontFamily: '"JetBrains Mono", monospace', fontWeight: 700 }}
                    >
                      LIVE
                    </span>
                  )}
                </div>
                <div className="text-[11px]" style={{ color: 'var(--text3)' }}>{activeContest?.name || 'Contest'}</div>
              </div>
            </div>

            <div className="flex items-center gap-1.5 flex-shrink-0">
              <button
                onClick={copyCode}
                className="flex items-center gap-1 px-2 py-1.5 text-xs transition-colors"
                style={{ background: 'rgba(124, 58, 237, 0.1)', borderRadius: 0, minHeight: 44 }}
              >
                <span className="font-mono font-semibold tracking-wider" style={{ color: '#7c3aed', fontFamily: '"JetBrains Mono", monospace' }}>
                  {room.code}
                </span>
                <span className="text-[10px]" style={{ color: 'rgba(124, 58, 237, 0.5)' }}>
                  {copied ? 'OK!' : 'Copy'}
                </span>
              </button>

              <button
                onClick={() => setShowMembers(!showMembers)}
                className="flex items-center gap-1 px-2 py-1.5 text-xs transition-colors"
                style={{ background: 'var(--surface)', borderRadius: 0, minHeight: 44 }}
              >
                <div className="flex -space-x-1">
                  {members.slice(0, 3).map(m => (
                    m.photoURL ? (
                      <img key={m.id} src={m.photoURL} alt="" className="w-4 h-4 rounded-full" style={{ border: '1px solid var(--bg)' }} referrerPolicy="no-referrer" />
                    ) : (
                      <div key={m.id} className="w-4 h-4 rounded-full flex items-center justify-center text-[8px]" style={{ background: 'var(--surface-hover)', border: '1px solid var(--bg)', color: 'var(--text3)' }}>
                        {m.name?.[0]}
                      </div>
                    )
                  ))}
                </div>
                <span style={{ color: 'var(--text3)' }}>{members.length}</span>
              </button>

              {user.isAdmin && (
                <button
                  onClick={() => setShowAdmin(!showAdmin)}
                  className="p-1.5 transition-colors"
                  style={{ background: 'var(--surface)', borderRadius: 0, minHeight: 32, minWidth: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <svg className="w-3.5 h-3.5" style={{ color: 'var(--gold)', opacity: 0.5 }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Members panel */}
          {showMembers && (
            <div className="mt-2.5 p-3 animate-slide-up" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
              <div className="font-display" style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '2px', color: 'var(--text3)', textTransform: 'uppercase', marginBottom: '8px' }}>
                MEMBERS ({members.length})
              </div>
              <div className="space-y-1.5">
                {members.map(m => (
                  <div key={m.id} className="flex items-center gap-2 text-sm">
                    {m.photoURL ? (
                      <img src={m.photoURL} alt="" className="w-5 h-5 rounded-full" referrerPolicy="no-referrer" />
                    ) : (
                      <div className="w-5 h-5 rounded-full flex items-center justify-center text-[10px]" style={{ background: 'var(--surface-hover)', color: 'var(--text3)' }}>{m.name?.[0]}</div>
                    )}
                    <span className="text-xs flex-1" style={{ color: 'var(--text2)' }}>{m.name}</span>
                    {m.id === room.createdBy && <span className="text-[10px] px-1.5 py-0.5" style={{ color: 'var(--gold)', background: 'rgba(251, 191, 36, 0.08)' }}>host</span>}
                    {m.id === user.id && <span className="text-[10px]" style={{ color: 'var(--text3)' }}>(you)</span>}
                    {isHost && m.id !== room.createdBy && m.id !== user.id && (
                      <button
                        onClick={() => handleKickMember(m.id)}
                        className="w-5 h-5 flex items-center justify-center transition-colors"
                        style={{ color: 'rgba(239,68,68,0.5)' }}
                        aria-label={`Kick ${m.name}`}
                        title="Kick member"
                      >
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
                {isHost && (
                  <button
                    onClick={handleDeleteRoom}
                    className="mt-2 w-full py-2 text-[10px] uppercase tracking-wider font-bold transition-colors"
                    style={{
                      background: 'rgba(239,68,68,0.1)',
                      color: '#ef4444',
                      border: '1px solid rgba(239,68,68,0.3)',
                      borderRadius: 0,
                      fontFamily: '"JetBrains Mono", monospace',
                      letterSpacing: '2px',
                    }}
                  >
                    Delete room
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Admin panel */}
          {showAdmin && user.isAdmin && (
            <div className="mt-2.5 p-3 animate-slide-up" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
              {activeContest?.closed && <div className="text-xs mb-2" style={{ color: 'var(--gold)' }}>Voting is closed</div>}
              <div className="flex flex-wrap gap-1.5">
                <button onClick={toggleContestStatus} className="px-2.5 py-1.5 text-xs font-medium" style={{ background: activeContest?.closed ? 'rgba(16,185,129,0.1)' : 'rgba(245,158,11,0.1)', color: activeContest?.closed ? '#10b981' : '#f59e0b', borderRadius: 0 }}>
                  {activeContest?.closed ? 'Open Voting' : 'Close Voting'}
                </button>
                <button onClick={handleClearCompetition} className="px-2.5 py-1.5 text-xs font-medium" style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444', borderRadius: 0 }}>Clear Scores</button>
                <button onClick={() => setPage('userManagement')} className="px-2.5 py-1.5 text-xs font-medium" style={{ background: 'var(--surface-hover)', color: 'var(--text3)', borderRadius: 0 }}>Users</button>
              </div>
            </div>
          )}
        </header>

        {/* Live ticker */}
        {tickerEntries !== null && (
          <div
            className="overflow-hidden whitespace-nowrap"
            style={{
              background: 'linear-gradient(90deg, #ff2d87, #7c3aed)',
              height: 28,
              lineHeight: '28px',
            }}
            role="marquee"
            aria-live="polite"
          >
            <div className="ticker-scroll inline-block">
              {(() => {
                const rankColor = (rank) => {
                  if (rank === 1) return '#fbbf24';
                  if (rank === 2) return '#e2e8f0';
                  if (rank === 3) return '#f97316';
                  if (rank <= 10) return '#06b6d4';
                  return 'rgba(255,255,255,0.7)';
                };
                const baseStyle = { fontFamily: '"JetBrains Mono", monospace', letterSpacing: '1px', fontSize: '11px' };
                const renderEntries = (arr) => {
                  if (!arr || !Array.isArray(arr.entries) || arr.entries.length === 0) {
                    return <span style={{ ...baseStyle, color: 'rgba(255,255,255,0.8)' }}>WAITING FOR VOTES...</span>;
                  }
                  return arr.entries.map((e, i) => (
                    <React.Fragment key={i}>
                      {i > 0 && <span style={{ ...baseStyle, color: 'rgba(255,255,255,0.5)' }}> {'\u2022'} </span>}
                      <span style={{ ...baseStyle, color: rankColor(e.rank), fontWeight: 700 }}>#{e.rank}</span>
                      <span style={{ ...baseStyle, color: '#fff' }}> {e.name} </span>
                      <span style={{ ...baseStyle, color: 'rgba(255,255,255,0.8)' }}>{e.avg}{e.arrow}</span>
                    </React.Fragment>
                  )).concat(arr.unvoted > 0 ? [
                    <span key="unvoted" style={{ ...baseStyle, color: 'rgba(255,255,255,0.5)' }}> {'\u2022'} {arr.unvoted} ACTS AWAITING VOTES</span>
                  ] : []);
                };
                return (
                  <span className="px-4">
                    {'\u25B8'} {renderEntries(tickerEntries)} {'\u25B8'} {renderEntries(tickerEntries)}
                  </span>
                );
              })()}
            </div>
          </div>
        )}

        {/* Now Playing banner */}
        {contestLive?.activeContestantId && !contestLive?.closed && (() => {
          const contestant = activeContest?.contestants?.find(c => String(c.id) === String(contestLive.activeContestantId));
          if (!contestant) return null;
          const cc = contestant.country ? COUNTRY_CODES[contestant.country] : null;
          return (
            <div style={{
              padding: '8px 16px', background: 'rgba(255, 45, 135, 0.1)',
              borderBottom: '2px solid var(--pink)', display: 'flex',
              alignItems: 'center', gap: '8px', fontSize: '12px',
            }}>
              <span style={{ color: 'var(--pink)', fontWeight: 700, fontSize: '10px', letterSpacing: '1px' }}>NOW PLAYING</span>
              {cc && <ReactCountryFlag countryCode={cc} svg style={{ width: '16px', height: '12px' }} />}
              <span style={{ fontWeight: 600, color: 'var(--text)' }}>{contestant.country || ''}</span>
              <span style={{ color: 'var(--text3)' }}>{contestant.name}</span>
            </div>
          );
        })()}

        {/* Emoji reaction bar — pinned together with header via parent sticky group */}
        <div className="flex flex-wrap items-center justify-center gap-1.5 py-2 px-3" style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg)' }}>
          {EMOJI_REACTIONS.map(r => (
            <button
              key={r.id}
              onClick={() => handleEmojiReact(r.id)}
              aria-label={r.label}
              className={`w-9 h-9 flex-shrink-0 flex items-center justify-center text-base transition-transform ${pressedEmoji === r.id ? 'emoji-press' : ''}`}
              style={{
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: '50%',
              }}
            >
              {r.emoji}
            </button>
          ))}
        </div>
        </div>
        {/* /sticky group */}

        {/* Diagonal divider */}
        <div className="divider-gradient" />

        {/* Content */}
        <div className={`px-4 pt-3 pb-safe transition-opacity duration-150 ${isLoading ? 'pointer-events-none opacity-50' : ''}`}>
          {activeTab === 'voting' && activeContest?.closed ? (
            <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
              <div className="font-display mb-3" style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--text)', fontFamily: '"Unbounded Variable", sans-serif', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Thank you for voting!
              </div>
              <div className="text-sm mb-2" style={{ color: 'var(--text2)', lineHeight: 1.6 }}>
                Voting is closed. Check the Rankings tab to see the results.
              </div>
              <div className="text-sm mb-4 font-display" style={{ color: 'var(--pink)', fontWeight: 700, letterSpacing: '0.03em' }}>
                See you next year! 🎤✨
              </div>
              <button
                onClick={() => setActiveTab('results')}
                className="px-5 py-2.5 text-white font-semibold text-sm transition-all active:scale-95"
                style={{ background: 'linear-gradient(90deg, #ff2d87, #7c3aed)', borderRadius: 0, minHeight: 44 }}
              >
                See Rankings
              </button>
            </div>
          ) : activeTab === 'voting' && !activeContest?.closed && !votingOpen ? (
            <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
              <div className="flex items-center gap-2 mb-6">
                <span className="live-pulse" style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: 'var(--pink)' }} />
                <span className="font-display text-xs tracking-widest" style={{ color: 'var(--pink)', fontFamily: '"Unbounded Variable", sans-serif', fontWeight: 700 }}>EUROBUDDY</span>
              </div>
              <div className="font-display mb-3" style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--text)', fontFamily: '"Unbounded Variable", sans-serif', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Show hasn't started yet
              </div>
              <div className="text-sm mb-6" style={{ color: 'var(--text2)', lineHeight: 1.6 }}>
                Stand by — voting opens when<br />the show begins
              </div>
              <div className="text-xs font-mono" style={{ color: 'var(--text3)', fontFamily: '"JetBrains Mono", monospace' }}>
                {activeContest?.name}
              </div>
            </div>
          ) : activeTab === 'voting' && !activeContest?.closed ? (
            <Voting
              activeContest={activeContest}
              currentContestant={null}
              updateScore={handleSubmitScore}
              getScore={getScore}
              setCurrentContestant={() => {}}
              isLoading={isLoading}
              reactions={reactions}
              lastChanged={lastChanged}
              allScores={allScores}
              user={user}
              activeContestantId={contestLive?.activeContestantId || null}
              activeOrder={contestLive?.activeOrder ?? null}
            />
          ) : activeTab === 'results' ? (
            <>
              {/* Contest switcher */}
              {(() => {
                const euro2026 = contests.filter(c => c.id.startsWith('euro2026'));
                if (euro2026.length <= 1) return null;
                const activeId = resultsContestId || room?.contestId;
                return (
                  <div className="flex items-center gap-1.5 mb-2 overflow-x-auto no-scrollbar pb-1">
                    {euro2026.map(c => {
                      const isActive = activeId === c.id;
                      const shortLabel = c.id === 'euro2026sf1' ? 'SF1' : c.id === 'euro2026sf2' ? 'SF2' : 'FINAL';
                      return (
                        <button
                          key={c.id}
                          onClick={() => setResultsContestId(c.id === room?.contestId ? null : c.id)}
                          className="flex-shrink-0 px-3 py-1 text-[10px] uppercase tracking-wider font-bold font-mono transition-colors"
                          style={{
                            fontFamily: '"JetBrains Mono", monospace',
                            letterSpacing: '2px',
                            background: isActive ? 'var(--surface)' : 'transparent',
                            color: isActive ? 'var(--text)' : 'var(--text3)',
                            border: isActive ? '1px solid var(--pink)' : '1px solid var(--border)',
                            borderRadius: 0,
                          }}
                        >
                          {shortLabel}
                        </button>
                      );
                    })}
                  </div>
                );
              })()}

              {/* Per-room ranking switcher — shown for own and past contests */}
              <div className="flex items-center gap-1.5 mb-3 overflow-x-auto no-scrollbar pb-1">
                {(() => {
                  const roomsForContest = myRooms.length > 0
                    ? myRooms
                    : [{ id: roomId, name: room?.name || 'This room', code: room?.code, memberIds }];
                  const chips = [
                    ...roomsForContest.map(r => ({ id: r.id, label: r.name, memberIds: r.memberIds })),
                    { id: 'global', label: 'EVERYONE', memberIds: null },
                  ];
                  return chips.map(chip => {
                    const isActive = rankingsScope === chip.id;
                    return (
                      <button
                        key={chip.id}
                        onClick={() => setRankingsScope(chip.id)}
                        className="flex-shrink-0 px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold font-mono transition-colors"
                        style={{
                          fontFamily: '"JetBrains Mono", monospace',
                          letterSpacing: '2px',
                          background: isActive ? 'linear-gradient(135deg, #ff2d87, #7c3aed)' : 'var(--surface)',
                          color: isActive ? '#fff' : 'var(--text3)',
                          border: '1px solid var(--border)',
                          borderRadius: 0,
                          maxWidth: '14rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={chip.label}
                      >
                        {chip.label}
                      </button>
                    );
                  });
                })()}
              </div>

              <Results
                activeContest={browseContestId ? contests.find(c => c.id === browseContestId) : activeContest}
                currentUser={user}
                handleDeleteVote={handleDeleteVote}
                allScores={browseContestId ? browseScores : allScores}
                memberIds={(() => {
                  if (rankingsScope === 'global') return null;
                  if (rankingsScope === roomId) return memberIds;
                  const chosen = myRooms.find(r => r.id === rankingsScope);
                  return chosen ? chosen.memberIds : memberIds;
                })()}
                isOwnRoom={rankingsScope !== 'global' || !!user?.isAdmin}
                reactions={reactions}
                lastChanged={lastChanged}
              />
            </>
          ) : (
            <MyVotesResults activeContest={activeContest} myScores={myScores} currentUser={user} />
          )}
        </div>

        {/* Bottom tabs */}
        <nav
          className="fixed bottom-0 inset-x-0 z-50"
          style={{
            background: 'var(--bg)',
            borderTop: '1px solid var(--border)',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            transform: 'translateZ(0)',
            WebkitTransform: 'translateZ(0)',
          }}
        >
          <div className="max-w-2xl mx-auto flex">
            {tabs.map(tab => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className="flex-1 flex flex-col items-center gap-0.5 py-2.5 transition-colors relative"
                  style={{
                    minHeight: 52,
                    opacity: tab.id === 'voting' && activeContest?.closed ? 0.4 : 1,
                    pointerEvents: tab.id === 'voting' && activeContest?.closed ? 'none' : 'auto',
                  }}
                >
                  {isActive && (
                    <div
                      className="absolute top-0 inset-x-0 h-0.5"
                      style={{ background: 'var(--gradient)' }}
                    />
                  )}
                  <TabIcon type={tab.icon} active={isActive} />
                  <span
                    className="font-display text-[10px]"
                    style={{
                      fontWeight: 600,
                      color: isActive ? 'var(--pink)' : 'var(--text3)',
                      letterSpacing: '1px',
                    }}
                  >
                    {tab.label}
                  </span>
                </button>
              );
            })}
          </div>
        </nav>
      </div>
    </div>
  );
};

export default RoomPage;
